import { Link2 } from 'lucide-react';

import { useEffect, useRef, useState } from 'react';
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { copyText } from './clipboard';

interface CopyLinkButtonProps {
  /** The URL to copy; never contains a token. */
  readonly url: string;
  /** Mobile sizing: 36px icon button instead of 40px. */
  readonly compact?: boolean;
}

/** How long the "copied" confirmation stays visible. */
const COPIED_CONFIRMATION_MS = 2000;

/**
 * Copies the room link to the clipboard with a visible confirmation. Falls back to the legacy
 * copy path when the Clipboard API is unavailable (non-secure origins). Rendered as a Verba
 * icon button; the confirmation text appears next to it (inside the control-bar pill or the
 * prejoin link row).
 */
export function CopyLinkButton({ url, compact = false }: CopyLinkButtonProps): JSX.Element {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const [failed, setFailed] = useState(false);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
      }
    };
  }, []);

  async function handleCopy(): Promise<void> {
    const ok = await copyText(url);
    setCopied(ok);
    setFailed(!ok);
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
    }
    timerRef.current = window.setTimeout(() => {
      setCopied(false);
      setFailed(false);
    }, COPIED_CONFIRMATION_MS);
  }

  return (
    <span className="relative inline-flex items-center gap-2">
      <button
        type="button"
        data-testid="copy-link"
        aria-label={t('controls.copy_link')}
        title={t('controls.copy_link')}
        onClick={() => {
          void handleCopy();
        }}
        className={
          compact
            ? 'grid h-9 w-9 shrink-0 place-items-center rounded-full border border-white/10 bg-zinc-800/50 text-zinc-300 transition-colors hover:bg-zinc-700 hover:text-white'
            : 'grid h-10 w-10 shrink-0 place-items-center rounded-full border border-white/10 bg-zinc-800/50 text-zinc-300 transition-colors hover:bg-zinc-700 hover:text-white'
        }
      >
        <Link2 className="h-4 w-4" />
      </button>
      {copied ? (
        <span role="status" className="text-xs text-emerald-300">
          {t('controls.copied')}
        </span>
      ) : null}
      {failed ? (
        <span role="status" className="text-xs text-red-300">
          {t('controls.copy_failed')}
        </span>
      ) : null}
    </span>
  );
}
